import { useEffect } from 'react'
import { RecordingItem } from '@/components/RecordingItem'
import { useMySubmissionsLoader } from '@/components/RecordingList/hooks/useMySubmissionsLoader'
import { SubmissionFilterBar, NoMatchesState, useSubmissionFilter } from '@/components/SubmissionFilter'
import { useTaxonomyStore } from '@/stores/taxonomyStore'
import { UserMenu } from '@/components/UserMenu/UserMenu'

/**
 * "My Submissions" history view: every submission the signed-in user has made,
 * sourced from AWS and merged with anything still held locally. Audio plays from
 * the on-device copy when present and streams from AWS otherwise (handled inside
 * RecordingItem). Reachable from the account menu; renders the local rows even
 * when offline.
 */
export function SubmissionsPage(): JSX.Element {
  const {
    recordings,
    localIds,
    isLoading,
    isLoadingMore,
    error,
    isOffline,
    hasMore,
    loadMore,
    reload,
    refreshLocal,
    currentPlayingId,
    handlePlaybackStart,
    handlePlaybackStop,
  } = useMySubmissionsLoader()

  // Submission rows reference asset/indication ids; without the taxonomy loaded
  // their labels can't resolve and the title would show raw ids. init() is
  // idempotent and reuses the recorder's cached taxonomy when present.
  const initTaxonomy = useTaxonomyStore((state) => state.init)
  useEffect(() => {
    void initTaxonomy()
  }, [initTaxonomy])

  const { filter, assetOptions, filteredRecordings, setAssetId, setPeriod, setRange, clear } =
    useSubmissionFilter(recordings)

  // The filter only makes sense once a settled, non-empty list exists.
  const showFilter = !isLoading && !error && recordings.length > 0

  return (
    <div className="min-h-screen bg-navy text-white">
      <div className="sticky top-0 z-10 mx-auto w-full max-w-md border-b border-navy-light bg-navy/95 backdrop-blur">
        <header className="flex items-center gap-3 px-4 pt-4 pb-3">
          <h1 className="flex-1 text-lg font-semibold">Submissions</h1>
          <UserMenu />
        </header>
        {showFilter && (
          <div className="px-4 pb-3.5">
            <SubmissionFilterBar
              assetId={filter.assetId}
              assetOptions={assetOptions}
              period={filter.period}
              range={filter.range}
              onAssetChange={setAssetId}
              onPeriodChange={setPeriod}
              onRangeChange={setRange}
            />
          </div>
        )}
      </div>

      <main className="mx-auto w-full max-w-md px-4 py-6 pb-24">
        {isOffline && (
          <div
            className="mb-4 rounded-lg border border-amber-400/30 bg-amber-400/10 px-3 py-2 text-xs text-amber-200"
            role="status"
          >
            Showing locally stored submissions only. Reconnect to load your full history.
          </div>
        )}

        {isLoading ? (
          <div
            className="w-full py-8 text-center text-sm text-gray-400"
            role="status"
            aria-live="polite"
            aria-label="Loading submissions"
          >
            <div className="flex flex-col items-center gap-2">
              <div
                className="h-6 w-6 animate-spin rounded-full border-2 border-neon-cyan border-t-transparent"
                aria-hidden="true"
              />
              <span>Loading submissions...</span>
            </div>
          </div>
        ) : error ? (
          <div className="w-full py-8 text-center" role="alert" aria-live="assertive">
            <p className="text-sm font-medium text-red-400">{error}</p>
            <button
              onClick={() => void reload()}
              className="mt-3 rounded-lg bg-neon-cyan/20 px-4 py-2 text-sm text-neon-cyan transition-colors hover:bg-neon-cyan/30 focus:outline-none focus:ring-2 focus:ring-neon-cyan"
              aria-label="Retry loading submissions"
            >
              Try Again
            </button>
          </div>
        ) : recordings.length === 0 ? (
          <div className="w-full py-12 text-center text-gray-400" role="status" aria-live="polite">
            <p className="text-sm font-medium">No submissions yet</p>
            <p className="mt-1 text-xs text-gray-500">Record audio or type a note to see it here</p>
          </div>
        ) : filteredRecordings.length === 0 ? (
          <NoMatchesState onClear={clear} />
        ) : (
          <>
            <div role="list" aria-label="My submissions" className="flex flex-col gap-3">
              {filteredRecordings.map((recording) => (
                <div key={recording.id} role="listitem">
                  <RecordingItem
                    recording={recording}
                    canDelete={localIds.has(recording.id)}
                    allowServerDelete
                    onDelete={() => void refreshLocal()}
                    onPlaybackStart={handlePlaybackStart}
                    onPlaybackStop={handlePlaybackStop}
                    currentPlayingId={currentPlayingId}
                  />
                </div>
              ))}
            </div>

            {hasMore && (
              <div className="mt-4 flex justify-center">
                <button
                  type="button"
                  onClick={() => void loadMore()}
                  disabled={isLoadingMore}
                  className="rounded-lg bg-navy-light px-5 py-2 text-sm text-white transition-colors hover:bg-navy-light/80 focus:outline-none focus:ring-2 focus:ring-neon-cyan disabled:cursor-not-allowed disabled:opacity-60"
                  aria-label="Load more submissions"
                >
                  {isLoadingMore ? 'Loading…' : 'Load more'}
                </button>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  )
}
